/**
 * ClientesPage - Refactored with hooks and sub-components
 *
 * Original: 2,034 lines → first refactor: ~540 lines → current: ~290 lines
 *
 * Extracted:
 * - useClienteImportModals (import modal state for hospitais, representantes, clientes)
 * - All other logic remains in dedicated hooks (useClienteData, useClienteModals, etc.)
 */
import React, { useState } from 'react';
import { Building2 } from 'lucide-react';

import { MainLayout } from '../components/MainLayout';
import clienteService from '../services/clienteService';
import ClienteFormModal from '../components/ClienteFormModal';
import ProdutoClienteFormModal from '../components/ProdutoClienteFormModal';
import RepresentanteFormModal from '../components/RepresentanteFormModal';
import VincularHospitaisModal from '../components/VincularHospitaisModal';
import VincularLocaisModal from '../components/cliente/VincularLocaisModal';
import { msg, MESSAGES } from '../utils/messages';
import { useConfirm } from '../hooks/useConfirm';

// Custom hooks
import {
  useClienteData,
  useClienteModals,
  useClienteProdutos,
  useClienteRepresentantes,
  useClienteHospitais,
  useClienteExcel,
} from '../hooks';
import { useClienteImportModals } from '../hooks/useClienteImportModals';

// Sub-components
import {
  ClienteHeader,
  ClienteTabs,
  DadosGeraisTab,
  ProdutosTab,
  RepresentantesTab,
  HospitaisTab,
  PontosContagemTab,
  GTINModal,
  RepresentanteFormatInfoModal,
  ImportRepresentanteModal,
  type ClienteTabType,
} from '../components/cliente';

import { ImportClienteHospitalModal } from '../components/cliente/ImportClienteHospitalModal';
import { ClienteHospitalFormatInfoModal } from '../components/cliente/ClienteHospitalFormatInfoModal';
import { ImportClientesModal } from '../components/cliente/ImportClientesModal';
import { ClienteImportFormatInfoModal } from '../components/cliente/ClienteImportFormatInfoModal';

const ClientesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ClienteTabType>('dados');
  const { confirm, ConfirmDialog } = useConfirm();

  // Data hook
  const {
    clientes,
    filteredClientes,
    selectedCliente,
    setSelectedCliente,
    loadClientes,
    refreshSelectedCliente,
    showInactive,
    setShowInactive,
  } = useClienteData();

  // Modals hook
  const {
    showClienteModal,
    setShowClienteModal,
    editingCliente,
    setEditingCliente,
    showProdutoModal,
    setShowProdutoModal,
    editingProduto,
    setEditingProduto,
    showGTINModal,
    setShowGTINModal,
    selectedProduto,
    setSelectedProduto,
    produtoGTINs,
    setProdutoGTINs,
    showRepresentanteModal,
    setShowRepresentanteModal,
    editingRepresentante,
    setEditingRepresentante,
    showVincularHospitaisModal,
    setShowVincularHospitaisModal,
    openCreateCliente,
    openEditCliente,
    openCreateProduto,
    openEditProduto,
    openCreateRepresentante,
    openEditRepresentante,
    closeGTINModal,
  } = useClienteModals();

  // Produtos hook
  const {
    produtos,
    loadingProdutos,
    produtosCount,
    activeCount,
    totalRowCount,
    showDeleted,
    setShowDeleted,
    pagination,
    setPagination,
    globalSearch,
    setGlobalSearch,
    sorting,
    setSorting,
    loadProdutos,
    handleSaveProduto,
    handleDeleteProduto,
    handleRestoreProduto,
    handleImportProdutos,
    handleImportGTINs,
    loadGTINs,
    handleAddGTIN,
    handleUpdateGTIN,
    handleDeleteGTIN,
  } = useClienteProdutos({ selectedCliente, activeTab });

  // Representantes hook
  const {
    representantes,
    loadingRepresentantes,
    loadRepresentantes,
    handleDeleteRepresentante,
  } = useClienteRepresentantes({ selectedCliente, activeTab });

  // Hospitais hook
  const {
    hospitais,
    loadingHospitais,
    searchHospital,
    loadHospitais,
    isEditingCodes,
    editedCodes,
    savingCodes,
    handleToggleEditMode,
    handleCodeChange,
    handleSaveCodes,
    handleDesvincularHospital,
  } = useClienteHospitais({ selectedCliente, activeTab });

  // Vincular Locais modal state
  const [showVincularLocaisModal, setShowVincularLocaisModal] = useState(false);
  const [selectedHospitalForLocais, setSelectedHospitalForLocais] = useState<{ id: number; nome: string } | null>(null);

  // Excel export/import hook
  const {
    exportProdutosToExcel,
    exportingProdutos,
    exportRepresentantesToExcel,
    exportHospitaisToExcel,
    downloadHospitaisTemplate,
    importHospitaisFromExcel,
    importingHospitais,
    downloadRepresentantesTemplate,
    importRepresentantesFromExcel,
    importingRepresentantes,
    importClientesFromExcel,
    importingClientes,
  } = useClienteExcel({ selectedCliente });

  // Import modals hook
  const importModals = useClienteImportModals({
    importHospitaisFromExcel,
    importRepresentantesFromExcel,
    importClientesFromExcel,
    onHospitaisImported: loadHospitais,
    onRepresentantesImported: loadRepresentantes,
    onClientesImported: loadClientes,
  });

  // Handle cliente selection
  const handleSelectCliente = (cliente: any) => {
    setSelectedCliente(cliente);
  };

  // Handle cliente delete
  const handleDeleteCliente = async () => {
    if (!selectedCliente) return;
    const confirmed = await confirm({
      title: 'Desativar Cliente',
      message: MESSAGES.CLIENT.CONFIRM_DEACTIVATE(selectedCliente.nome_fantasia || selectedCliente.razao_social),
      variant: 'danger',
      confirmText: 'Desativar',
    });
    if (!confirmed) return;
    try {
      await clienteService.deleteCliente(selectedCliente.id);
      loadClientes();
      setSelectedCliente(null);
      msg.success(MESSAGES.CLIENT.DEACTIVATED);
    } catch (err: any) {
      msg.error(MESSAGES.CLIENT.DEACTIVATE_ERROR);
      console.error(err);
    }
  };

  // Handle delete from modal
  const handleDeleteFromModal = async (id: number) => {
    await clienteService.deleteCliente(id);
    loadClientes();
    setSelectedCliente(null);
    setShowClienteModal(false);
  };

  // Handle modal success
  const handleModalSuccess = async () => {
    await loadClientes();
    if (selectedCliente && editingCliente && selectedCliente.id === editingCliente.id) {
      await refreshSelectedCliente();
    }
  };

  // Handle manage GTINs
  const handleManageGTINs = async (produto: any) => {
    setSelectedProduto(produto);
    const gtins = await loadGTINs(produto);
    setProdutoGTINs(gtins);
    setShowGTINModal(true);
  };

  const onAddGTIN = async (gtinData: any) => {
    if (!selectedProduto) return;
    const gtins = await handleAddGTIN(selectedProduto, gtinData);
    setProdutoGTINs(gtins);
  };

  const onUpdateGTIN = async (gtinId: number, gtinData: any) => {
    if (!selectedProduto) return;
    const gtins = await handleUpdateGTIN(selectedProduto, gtinId, gtinData);
    if (gtins.length > 0 || gtins) setProdutoGTINs(gtins);
  };

  const onDeleteGTIN = async (gtinId: number) => {
    if (!selectedProduto) return;
    const gtins = await handleDeleteGTIN(selectedProduto, gtinId, confirm);
    if (gtins.length > 0 || gtins) setProdutoGTINs(gtins);
  };

  return (
    <>
      <ConfirmDialog />
      <MainLayout>
        <div className="p-6">
          {/* Header */}
          <ClienteHeader
            clientes={clientes}
            filteredClientes={filteredClientes}
            selectedCliente={selectedCliente}
            onSelectCliente={handleSelectCliente}
            onCreateNew={openCreateCliente}
            onEdit={() => selectedCliente && openEditCliente(selectedCliente)}
            onOpenImportModal={() => importModals.setShowImportClientesModal(true)}
            onOpenFormatInfo={() => importModals.setShowClienteFormatInfoModal(true)}
            produtosCount={produtosCount}
            representantes={representantes}
            hospitais={hospitais}
            showInactive={showInactive}
            onShowInactiveChange={setShowInactive}
          />

          {/* Main Content */}
          <div>
            {selectedCliente ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
                <ClienteTabs activeTab={activeTab} setActiveTab={setActiveTab} />
                <div className="p-4">
                  {activeTab === 'dados' && (
                    <DadosGeraisTab
                      cliente={selectedCliente}
                      onClienteUpdate={(updated) => setSelectedCliente(updated)}
                    />
                  )}
                  {activeTab === 'produtos' && (
                    <ProdutosTab
                      produtos={produtos}
                      loading={loadingProdutos}
                      activeCount={activeCount}
                      totalRowCount={totalRowCount}
                      pagination={pagination}
                      setPagination={setPagination}
                      globalSearch={globalSearch}
                      setGlobalSearch={setGlobalSearch}
                      sorting={sorting}
                      setSorting={setSorting}
                      onNewProduto={openCreateProduto}
                      onEditProduto={openEditProduto}
                      onDeleteProduto={(produto) => handleDeleteProduto(produto, confirm)}
                      onRestoreProduto={(produto) => handleRestoreProduto(produto, confirm)}
                      showDeleted={showDeleted}
                      onToggleShowDeleted={setShowDeleted}
                      onManageGTINs={handleManageGTINs}
                      onImportProdutos={handleImportProdutos}
                      onImportGTINs={handleImportGTINs}
                      onExportExcel={() => exportProdutosToExcel()}
                      exportingProdutos={exportingProdutos}
                      clienteId={selectedCliente?.id ?? null}
                      clienteNome={selectedCliente?.nome_fantasia || selectedCliente?.razao_social}
                    />
                  )}
                  {activeTab === 'pontos_contagem' && (
                    <PontosContagemTab
                      hospitais={hospitais}
                      loading={loadingHospitais}
                      onOpenVincularLocais={(hospitalId, hospitalNome) => {
                        setSelectedHospitalForLocais({ id: hospitalId, nome: hospitalNome });
                        setShowVincularLocaisModal(true);
                      }}
                    />
                  )}
                  {activeTab === 'representantes' && (
                    <RepresentantesTab
                      representantes={representantes}
                      loading={loadingRepresentantes}
                      onNewRepresentante={openCreateRepresentante}
                      onEditRepresentante={openEditRepresentante}
                      onDeleteRepresentante={(id) => handleDeleteRepresentante(id, confirm)}
                      onExportExcel={() => exportRepresentantesToExcel(representantes)}
                      onOpenImportModal={() => importModals.setShowImportRepresentantesModal(true)}
                      onOpenFormatInfo={() => importModals.setShowRepresentanteFormatInfoModal(true)}
                    />
                  )}
                  {activeTab === 'hospitais' && (
                    <HospitaisTab
                      hospitais={hospitais}
                      loading={loadingHospitais}
                      isEditingCodes={isEditingCodes}
                      editedCodes={editedCodes}
                      savingCodes={savingCodes}
                      searchHospital={searchHospital}
                      onToggleEditMode={handleToggleEditMode}
                      onCodeChange={handleCodeChange}
                      onSaveCodes={handleSaveCodes}
                      onDesvincularHospital={(id) => handleDesvincularHospital(id, confirm)}
                      onOpenVincularModal={() => setShowVincularHospitaisModal(true)}
                      onExportExcel={() => exportHospitaisToExcel(hospitais)}
                      onOpenImportModal={() => importModals.setShowImportHospitaisModal(true)}
                      onOpenFormatInfo={() => importModals.setShowFormatInfoModal(true)}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Selecione um cliente para ver os detalhes
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Modals */}
          <ClienteFormModal
            isOpen={showClienteModal}
            onClose={() => setShowClienteModal(false)}
            onSuccess={handleModalSuccess}
            onDelete={handleDeleteFromModal}
            cliente={editingCliente}
          />

          {showProdutoModal && selectedCliente && (
            <ProdutoClienteFormModal
              clienteId={selectedCliente.id}
              producto={editingProduto}
              onClose={() => { setShowProdutoModal(false); setEditingProduto(null); }}
              onSuccess={() => { loadProdutos(); setShowProdutoModal(false); setEditingProduto(null); }}
            />
          )}

          {showRepresentanteModal && selectedCliente && (
            <RepresentanteFormModal
              representante={editingRepresentante}
              clienteId={selectedCliente.id}
              onClose={() => { setShowRepresentanteModal(false); setEditingRepresentante(null); }}
              onSave={() => { setShowRepresentanteModal(false); setEditingRepresentante(null); loadRepresentantes(); }}
            />
          )}

          {showVincularHospitaisModal && selectedCliente && (
            <VincularHospitaisModal
              clienteId={selectedCliente.id}
              hospitaisVinculados={hospitais.map(ch => ch.hospital_id)}
              onClose={() => setShowVincularHospitaisModal(false)}
              onSave={() => { setShowVincularHospitaisModal(false); loadHospitais(); }}
            />
          )}

          {showVincularLocaisModal && selectedCliente && selectedHospitalForLocais && (
            <VincularLocaisModal
              clienteId={selectedCliente.id}
              hospitalId={selectedHospitalForLocais.id}
              hospitalNome={selectedHospitalForLocais.nome}
              onClose={() => { setShowVincularLocaisModal(false); setSelectedHospitalForLocais(null); loadHospitais(true); }}
              onSave={() => { setShowVincularLocaisModal(false); setSelectedHospitalForLocais(null); }}
            />
          )}

          <GTINModal
            show={showGTINModal}
            produto={selectedProduto}
            gtins={produtoGTINs}
            onAddGTIN={onAddGTIN}
            onUpdateGTIN={onUpdateGTIN}
            onDeleteGTIN={onDeleteGTIN}
            onClose={closeGTINModal}
          />

          {/* Import Hospitais */}
          <ImportClienteHospitalModal
            show={importModals.hospitaisModal.show}
            importFile={importModals.hospitaisModal.file}
            importing={importingHospitais}
            importResult={importModals.hospitaisModal.result}
            onFileChange={importModals.setImportFile}
            onDownloadTemplate={downloadHospitaisTemplate}
            onImport={importModals.handleImportHospitais}
            onClose={importModals.handleCloseImportModal}
          />
          <ClienteHospitalFormatInfoModal
            show={importModals.showFormatInfoModal}
            onClose={() => importModals.setShowFormatInfoModal(false)}
          />

          {/* Import Representantes */}
          <ImportRepresentanteModal
            show={importModals.representantesModal.show}
            importFile={importModals.representantesModal.file}
            importing={importingRepresentantes}
            importResult={importModals.representantesModal.result}
            onFileChange={importModals.setImportRepresentanteFile}
            onDownloadTemplate={downloadRepresentantesTemplate}
            onImport={importModals.handleImportRepresentantes}
            onClose={importModals.handleCloseImportRepresentantesModal}
          />
          <RepresentanteFormatInfoModal
            show={importModals.showRepresentanteFormatInfoModal}
            onClose={() => importModals.setShowRepresentanteFormatInfoModal(false)}
          />

          {/* Import Clientes (Bulk) */}
          <ImportClientesModal
            show={importModals.clientesModal.show}
            importFile={importModals.clientesModal.file}
            importing={importingClientes}
            importResult={importModals.clientesModal.result}
            onFileChange={importModals.setImportClienteFile}
            onImport={importModals.handleImportClientes}
            onClose={importModals.handleCloseImportClientesModal}
          />
          <ClienteImportFormatInfoModal
            show={importModals.showClienteFormatInfoModal}
            onClose={() => importModals.setShowClienteFormatInfoModal(false)}
          />
        </div>
      </MainLayout>
    </>
  );
};

export default ClientesPage;
